/* eslint-disable */
// Temporarily disable TypeScript and ESLint checks for this file while
// keeping runtime behavior unchanged. This reduces noisy compile errors
// caused by large legacy component typings and custom third-party APIs.
// Remove these flags after the file is incrementally typed.
// @ts-nocheck
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Tab,
  Tabs,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useLocation } from "react-router-dom";
import React from "react";
import { DataContext } from "../contexts/DataProvider";
import { fetchApps, updateAppSetting, saveFileTypes } from "../services/apps.service";
import { useImpersonation } from "../hooks/useImpersonation";
import { useAlert } from "../functions/useAlert";
import UploadDocumentTemplate from "../admin/UploadDocumentTemplate";
import Container from "../base/Container";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import Joyride, { CallBackProps } from "react-joyride";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ProductFeedbackAdminView from "../components/Admin/ProductFeedbackAdminView";
import OrgFeedbackView from "../components/Admin/OrgFeedbackView";
import BillingExemptBanner from "./components/BillingExemptBanner";
import {
  organisationsGlobalAdminTourSteps,
  organisationsOrgAdminTourSteps,
} from "../tours";
import { useConnectors } from "./hooks/useConnectors";
import { useOntology } from "./hooks/useOntology";
import { useOrganisations } from "./hooks/useOrganisations";
import { useStorageProviders } from "./hooks/useStorageProviders";
import { useUsersManagement } from "./hooks/useUsersManagement";
import PaymentsTab from "../stripe/PaymentsTab";
import OrganisationProfileNew from "./OrganisationProfileNew";
import ManageOrgsPanel from "./components/ManageOrgsPanel";
import GlobalAdminUsersPanel from "./components/GlobalAdminUsersPanel";
import OrgAdminUsersPanel from "./components/OrgAdminUsersPanel";
import ConnectorsTabPanel from "./components/ConnectorsTabPanel";
import OntologyTabPanel from "./components/OntologyTabPanel";
import OrgFormDialog from "./components/OrgFormDialog";
import UserFormDialog from "./components/UserFormDialog";
import ConnectorConfigModal from "./components/ConnectorConfigModal";
import StorageProviderDialog from "./components/StorageProviderDialog";
import SharePointSiteDialog from "./components/SharePointSiteDialog";
import ConfirmSeatDialog from "./components/ConfirmSeatDialog";
import DeleteOrgDialog from "./components/DeleteOrgDialog";
import ToggleConnectorDialog from "./components/ToggleConnectorDialog";
import FileTypesDrawer from "./components/FileTypesDrawer";
import { useSessionState } from "../hooks/useSessionState";
import { WorkflowsTab } from "../workflows";

const FragmentWrapper = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
);

const Organisations = ({
  isAdmin,
  view,
}: {
  isAdmin: boolean;
  view?:
    | "global-admin-orgs"
    | "global-admin-ontology"
    | "global-admin-feedback";
}) => {
  const [open, setOpen] = useState(false);
  const [setAlert] = useAlert();
  const { login }: any = useContext(DataContext);
  const { enter: enterImpersonation, isImpersonating } = useImpersonation();

  const location = useLocation();
  // Namespaced by `view` — the standalone /admin page and each embedded Global Admin
  // org sub-view share this component but shouldn't share a persisted tab index.
  const [tabValue, setTabValue] = useSessionState(view ? `admin:tabValue:${view}` : "admin:tabValue", 0);
  const [options, setOptions] = useState<any[]>([]);
  const [organisationList, setOrganisationsList] = useState<any[]>([]);

  const [selectedOrganization, setSelectedOrganization] = useState("");

  const usersManagement = useUsersManagement({
    organisationList,
    selectedOrganization,
    onUserCreated: () => fetchOrganisations(),
  });
  const {
    users, setUsers, allUsers, setAllUsers, optionsForUsers, setOptionsForUsers,
    openUserRow, setOpenUserRow, searchUserText, setSearchUserText,
    searchText, setSearchText, searchUser, setSearchUser, userSearchText, setUserSearchText,
    validationErrors, setValidationErrors, userErrors, setUserErrors,
    passwordModalOpen, setPasswordModalOpen, passwordModalUserId, setPasswordModalUserId,
    passwordModalValues, setPasswordModalValues, passwordModalError, setPasswordModalError,
    deleteConfirmOpen, setDeleteConfirmOpen, deleteUserId, deleteUserName,
    confirmPasswords, setConfirmPasswords,
    form, setForm, openModalUser, setOpenModalForUser, editingUser, setEditingUser,
    addUserError, setAddUserError, userForms, setUserForms, userFormsErrors, setUserFormsErrors,
    emailDuplicateWarnings, setEmailDuplicateWarnings, expandedUserIndices,
    manualPassword, setManualPassword, showAddUserPassword, setShowAddUserPassword,
    userSubmitting, confirmSeatModal, setConfirmSeatModal, confirmSeatModalError, confirmSeatLoading,
    proratedAmount, isPremium, setIsPremium, manualSeatCount,
    isUserCardExpanded, toggleUserCard, addUserEntry, removeUserEntry,
    updateUserEntry, checkEmailDuplicate,
    handleUserInputChange, handleSaveUser, handleSetPassword, handleExpandUser,
    handleSearchUser, handleOpenModal, handleChangeForUser, validateUserEntry,
    handleSubmitUser, handleConfirmSeatAddition, handleResendInvite,
    handleDeleteUser, confirmDeleteUser, fetchUsers, fetchProratedAmount,
  } = usersManagement;

  const [checked, setChecked] = useState([
    "Statement of Advice (SOW)",
    "File Note",
  ]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedAppSetting, setSelectedAppSetting] = useState<
    Record<string, boolean>
  >({});
  const [apps, setApps] = useState<any[]>([]);
  const [originalAppSetting, setOriginalAppSetting] = useState<
    Record<string, boolean>
  >({});
  const [appId, setAppId] = useState<string>("");

  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const isGlobalAdmin = login?.data?.role === "global admin";
  // When rendering as a global-admin tab, map internal tab 0/1/2 → original slot 5/6/7
  const effectiveTabValue =
    view === "global-admin-orgs"
      ? 5
      : view === "global-admin-ontology"
        ? 6
        : view === "global-admin-feedback"
          ? 7
          : tabValue;

  const [currentSteps, setCurrentSteps] = useState<Step[]>([]);

  const [toursSeen, setToursSeen] = useState(() => {
    try {
      const seen = localStorage.getItem("adminToursSeen");
      return seen ? JSON.parse(seen) : {};
    } catch (error) {
      console.error("Failed to parse adminToursSeen from localStorage", error);
      return {};
    }
  });

  const completeTourSteps = isGlobalAdmin
    ? organisationsGlobalAdminTourSteps
    : organisationsOrgAdminTourSteps;

  const startCompleteTour = () => {
    // If global admin and no org selected, select the first one
    if (
      isGlobalAdmin &&
      selectedOrganization === "All Organisation" &&
      organisationList &&
      organisationList.length > 0
    ) {
      setSelectedOrganization(organisationList[0]._id);
    }

    // Start from the first tab
    setTabValue(0);

    // Set the complete tour steps
    setCurrentSteps(completeTourSteps);
    setRunTour(true);
  };

  // selectedOrgObj is derived here so it's available for the storage hook
  const selectedOrgObj = organisationList?.find(
    (org) => org._id === selectedOrganization,
  );

  const {
    storageProvider, setStorageProvider,
    isSwitchingStorage,
    storageConfirmDialog, setStorageConfirmDialog,
    pendingStorageProvider, setPendingStorageProvider,
    storageStats,
    isMigrationActionLoading,
    availableDrives,
    isFetchingDrives,
    selectedDriveId, setSelectedDriveId,
    isSiteChangeDialogOpen, setIsSiteChangeDialogOpen,
    pendingDriveSelection, setPendingDriveSelection,
    fetchStorageStats,
    fetchSharePointDrives,
    openSiteChangeDialog,
    handleConfirmSiteChange,
    handleMigrationAction,
    handleToggleStorageProvider,
    handleStorageProviderClick,
    confirmAndStartMigration,
  } = useStorageProviders({
    selectedOrganization,
    selectedOrgObj,
    fetchOrganisations: () => fetchOrganisations(),
  });

  const {
    ontologyTabValue, setOntologyTabValue,
    ontologyFileContent, setOntologyFileContent,
    shaclFileContent, setShaclFileContent,
    lastSavedOntology,
    ontologyMeta, shaclMeta,
    lastSavedShacl,
    isEditing, setIsEditing,
    isFetching,
    isSaving,
    uploadingType,
    ontologyInputRef, shaclInputRef,
    ontologyHasUnsavedChanges, shaclHasUnsavedChanges, currentTabHasUnsavedChanges,
    fetchGraphData, fetchGraphDbOntology, fetchOntologyData,
    handleSaveOntology, handleFileUpload,
  } = useOntology({ selectedOrganization });

  const {
    connectorTabValue, setConnectorTabValue,
    isConnectorModalOpen, setIsConnectorModalOpen,
    currentConnector, setCurrentConnector,
    connectorInputs, setConnectorInputs,
    originalConnectorInputs, setOriginalConnectorInputs,
    connectorAuthorization, setConnectorAuthorization,
    isConnectorLoading,
    confirmationAction, setConfirmationAction,
    isEmailEnabled,
    isOutlookContactsEnabled,
    isAutoRecordingEnabled,
    autoRecordingSetupOpen, setAutoRecordingSetupOpen,
    handleChangeForAzure,
    handleCloseConnectorModal,
    handleCardClick,
    handleAdviserLogicToggle,
    handleToggle,
    handleConfirmToggle,
    toggleEmailStatus,
    toggleAutoRecordingStatus,
    toggleOutlookContactsStatus,
    handleConnectMicrosoft365,
    handleDisconnectMicrosoft365,
    handleAuthorizeConnector,
  } = useConnectors({
    selectedOrganization,
    onOrganizationChange: setSelectedOrganization,
    onStorageProviderChange: setStorageProvider,
  });

  const {
    editingData, setEditingData,
    openModal, setOpenModal,
    editData, setEditData,
    openRow, setOpenRow,
    organisation, setOrganisation,
    orgValidationErrors, setOrgValidationErrors,
    deleteOrgConfirmOpen, setDeleteOrgConfirmOpen,
    deleteOrgId, setDeleteOrgId,
    deleteOrgName, setDeleteOrgName,
    deleteOrgConfirmText, setDeleteOrgConfirmText,
    anchorEl, openButton,
    handleOpenMenu, handleCloseMenu,
    handleClick, handleClose,
    handleClickOrganisation, handleCloseModal,
    handleChange, handleInputChange,
    handleExpand, handleCancel,
    fetchOrganisations,
    handleSubmit, handleSave,
    handleReassignOrganisation,
    handleDelete, confirmDeleteOrganisation, cancelDeleteOrganisation,
  } = useOrganisations({
    selectedOrganization,
    organisationList,
    onOrganisationsLoaded: (orgs) => {
      setOrganisationsList(orgs);
      setOptions(orgs);
    },
    onStorageProviderChange: setStorageProvider,
  });

  // Fetch stats when the user opens the Microsoft 365 modal
  useEffect(() => {
    if (isConnectorModalOpen && currentConnector?.id === "microsoft_365") {
      fetchStorageStats();
    }
  }, [isConnectorModalOpen, selectedOrganization, currentConnector]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status, type, step, index } = data;

    // Handle back button navigation first
    if (action === "prev" && type === "step:after") {
      setTourStepIndex(index - 1);
      return; // Exit early to prevent any other logic
    }

    // Handle forward navigation
    if (action === "next" && type === "step:after") {
      setTourStepIndex(index + 1);
      return; // Exit early after handling next
    }

    // Reset tour when user closes, skips, finishes, or clicks outside
    if (
      ["close", "skip", "finished"].includes(status as any) ||
      action === "close" ||
      action === "skip"
    ) {
      setRunTour(false);
      setTourStepIndex(0); // Reset to first step
      return;
    }

    // Handle tab switching during the tour
    if (type === "step:before") {
      const { meta }: any = step || {};
      if (meta?.action) {
        // Handle tab switching for complete tour
        if (meta.action.startsWith("switch-to-tab-")) {
          const tabIndex = parseInt(meta.action.split("-").pop() || "0");
          setTimeout(() => {
            setTabValue(tabIndex);
          }, 300);
        }
      }
    }
  };

  const fetchAppData = async () => {
    try {
      const response: any = await fetchApps();
      setApps(response);
    } catch {
      // non-critical — apps list failure doesn't block the page
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppData();
  }, []);

  const hasChanges = (
    original: Record<string, boolean>,
    updated: Record<string, boolean>,
  ): boolean => Object.keys(original).some((key) => original[key] !== updated[key]);

  const handleSaveFileTypes = async () => {
    const orgId = login.data?.orgId;
    try {
      if (hasChanges(originalAppSetting, selectedAppSetting)) {
        await updateAppSetting(appId, selectedAppSetting);
        await fetchAppData();
      }
      const resp: any = await saveFileTypes(checked, orgId);
      if (resp.hasError) throw new Error(resp.message);
      setAlert("File types saved successfully", "success");
      setOpen(false);
    } catch (err: any) {
      setAlert(err.message || "Failed to save file types", "error");
    }
  };

  const fileTypes = Object.keys(selectedAppSetting);

  useEffect(() => {
    // Auto-select organization based on user role
    if (!organisationList || organisationList.length === 0) return;

    // For global admins: show "All Organisation" option
    if (isGlobalAdmin) {
      setSelectedOrganization("All Organisation");
    } else {
      // For org-admins: automatically select their own organization and load connector data
      const userOrgId = login?.data?.orgId;
      if (userOrgId) {
        const userOrg = organisationList.find((org) => org._id === userOrgId);
        if (userOrg) {
          setSelectedOrganization(userOrgId);
          handleChangeForAzure({ target: { value: userOrgId } });
        }
      }
    }
  }, [organisationList, isGlobalAdmin, login?.data?.orgId]);

  useEffect(() => {
    const state = location.state as { defaultTabIndex?: number };
    const params = new URLSearchParams(location.search);
    const tabQueryParam = params.get("tab");
    const orgIdQueryParam = params.get("orgId");

    const maxTabIndex = login?.data?.role === "global admin" ? 7 : 4;

    // ✅ Set selectedOrganization from URL parameter if present
    if (orgIdQueryParam && organisationList.length > 0) {
      const orgExists = organisationList.some(
        (org) => org._id === orgIdQueryParam,
      );
      if (orgExists) {
        setSelectedOrganization(orgIdQueryParam);
      }
    }

    if (
      state?.defaultTabIndex !== undefined &&
      typeof state.defaultTabIndex === "number"
    ) {
      if (state.defaultTabIndex >= 0 && state.defaultTabIndex <= maxTabIndex) {
        setTabValue(state.defaultTabIndex);
        navigate(location.pathname, { replace: true });
      } else {
        console.warn(
          "Invalid defaultTabIndex received in location state:",
          state.defaultTabIndex,
        );
      }
    } else if (tabQueryParam === "templates") {
      // Global admin: Manage Template is at index 3
      // Org admin: Manage Template is at index 3
      const templatesTabIndex = login?.data?.role === "global admin" ? 3 : 3;
      setTabValue(templatesTabIndex);
    } else if (tabQueryParam === "connectors") {
      // Connectors tab is at index 2 for global admin, index 2 for org admin
      const connectorsTabIndex = login?.data?.role === "global admin" ? 2 : 2;
      setTabValue(connectorsTabIndex);
    } else if (tabQueryParam === "payments") {
      // Global admin: Payments is at index 4
      // Org admin: Payments is at index 4
      const paymentsTabIndex = login?.data?.role === "global admin" ? 4 : 4;
      setTabValue(paymentsTabIndex);
    } else if (tabQueryParam === "workflows") {
      setTabValue(login?.data?.role === "orgadmin" ? 6 : 5);
    }

    // Handle OAuth connector error messages
    const errorParam = params.get("error");
    const messageParam = params.get("message");

    if (errorParam && messageParam) {
      const decodedMessage = decodeURIComponent(messageParam);

      if (errorParam === "microsoft365_required") {
        setAlert(
          "Microsoft 365 connector must be set up first to enable Outlook contacts",
          "warning",
        );
      } else if (errorParam === "outlook_setup_failed") {
        setAlert(`Outlook setup failed: ${decodedMessage}`, "error");
      } else {
        setAlert(decodedMessage, "error");
      }

      navigate(location.pathname, { replace: true });
    }

    // Handle successful OAuth connections
    const outlookConnected = params.get("outlook_connected");
    if (outlookConnected === "true") {
      setAlert("Microsoft Outlook contacts are now available!", "success");

      if (selectedOrganization && selectedOrganization !== "All Organisation") {
        handleChangeForAzure({ target: { value: selectedOrganization } });
      }

      navigate(location.pathname, { replace: true });
    }

    // Handle Microsoft 365 org-level OAuth result
    const ms365Connected = params.get("ms365_connected");
    const ms365OrgId = params.get("ms365_org");
    const ms365Error = params.get("ms365_error");

    if (ms365Connected === "true") {
      setAlert("Microsoft 365 connected successfully!", "success");

      // ms365_org carries the orgId through the OAuth redirect so we can re-select it
      const orgToSelect = ms365OrgId || selectedOrganization;
      if (orgToSelect && orgToSelect !== "All Organisation") {
        setSelectedOrganization(orgToSelect);
        handleChangeForAzure({ target: { value: orgToSelect } });
      }

      navigate(location.pathname, { replace: true });
    }

    if (ms365Error) {
      const decoded = decodeURIComponent(ms365Error);
      const friendlyMessage =
        decoded === "access_denied"
          ? "Microsoft 365 connection was cancelled. You can try again anytime."
          : decoded === "domain_mismatch"
            ? "This Microsoft 365 account belongs to a different organisation. Please connect using an account from your own Microsoft 365 tenant."
            : `Microsoft 365 connection failed: ${decoded}`;
      setAlert(friendlyMessage, "error");

      navigate(location.pathname, { replace: true });
    }
  }, [
    location.state,
    location.search,
    login?.data?.role,
    organisationList,
    setAlert,
  ]);


  const toggleCheck = (item: string) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item],
    );
  };


  useEffect(() => {
    fetchOrganisations();
  }, []);


  const filteredData = editingData?.filter((row) => {
    const search = searchText.toLowerCase();
    return (
      !search ||
      row.name?.toLowerCase().includes(search) ||
      row.billingContactPhone?.toLowerCase().includes(search) ||
      row.billingContactEmail?.toLowerCase().includes(search)
    );
  });
  const filteredUsers = users.filter((user) => {
    if (
      selectedOrganization !== "All Organisation" &&
      selectedOrganization &&
      user.orgId !== selectedOrganization
    ) {
      return false;
    }

    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const phone = user.phone?.toLowerCase() || "";
    const email = user.email?.toLowerCase() || "";

    return (
      fullName.includes(userSearchText.toLowerCase()) ||
      phone.includes(userSearchText.toLowerCase()) ||
      email.includes(userSearchText.toLowerCase())
    );
  });

  const formatKey = (key: string) => {
    if (key === "statementOfAdvice") return "Statement of Advice (SOW)";
    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
  };

  // Effect: Fetch TTL from Python when entering Manage Ontology tab
  useEffect(() => {
    if (effectiveTabValue === 6 && login?.data?.role === "global admin") {
      setIsEditing(false);
      fetchGraphDbOntology();
    }
  }, [tabValue, selectedOrganization]);

  // When rendered as a tab inside GlobalAdminPage, skip Container and only show the relevant content
  if (view === "global-admin-feedback") {
    return (
      <div id="feedback-tab">
        <ProductFeedbackAdminView />
      </div>
    );
  }

  const ContainerOrFragment = view ? FragmentWrapper : Container;

  return (
    <>
      <ContainerOrFragment>
        {!view && (
          <Joyride
            continuous
            run={runTour}
            steps={currentSteps}
            callback={handleJoyrideCallback}
            showSkipButton
            stepIndex={tourStepIndex}
            disableOverlayClose={false}
            disableCloseOnEsc={false}
            spotlightClicks={false}
            scrollOffset={80}
            spotlightPadding={3}
            disableScrolling={true}
            disableScrollParentFix={true}
            floaterProps={{
              disableAnimation: false,
              styles: {
                floater: {
                  filter: "none",
                },
              },
            }}
            styles={{
              options: {
                primaryColor: "#1976d2",
                zIndex: 1301,
              },
              tooltip: {
                borderRadius: "12px",
                padding: "16px 20px",
              },
              buttonNext: {
                backgroundColor: "#1976d2",
                borderRadius: "8px",
              },
              buttonBack: {
                color: "#555",
              },
            }}
          />
        )}
        <Box sx={{ width: "100%", p: 2 }}>
          {/* Global org filter — irrelevant on Workflows (tab 5): workflows run
              against the logged-in admin's own org via auth context, not a
              picked org, so the dropdown doesn't apply there. */}
          {login?.data?.role === "global admin" && !view && tabValue !== 5 && (
            <FormControl
              id="org-filter-dropdown"
              style={{ marginTop: "20px", marginBottom: "20px" }}
              variant="outlined"
              fullWidth
            >
              <InputLabel id="org-select-label">Organisation</InputLabel>
              <Select
                labelId="org-select-label"
                id="org-select"
                value={selectedOrganization}
                onChange={handleChangeForAzure}
                label="Select Organisation"
              >
                <MenuItem value="All Organisation">All Organisation</MenuItem>
                {organisationList?.map((org) => (
                  <MenuItem key={org._id} value={org._id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Tabs Row with Tour Button — hidden in global-admin tab views */}
          {!view && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: 1,
                borderColor: "divider",
                mb: 2,
              }}
            >
              {/* Tabs Container */}
              <Box sx={{ maxWidth: "calc(100% - 50px)", flexGrow: 1 }}>
                {view ? (
                  <Tabs
                    id="admin-tabs-container"
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      label="Manage Organisations"
                      id="org-details-tab-button"
                    />
                    <Tab
                      label="Manage Ontology"
                      id="manage-ontology-tab-button"
                    />
                  </Tabs>
                ) : (
                  <Tabs
                    id="admin-tabs-container"
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      label="Organisation Profile"
                      id="org-profile-tab-button"
                    />
                    <Tab label="Users" id="users-tab-button" />
                    <Tab label="Connectors" id="connectors-tab-button" />
                    <Tab
                      label="Manage Template"
                      id="manage-template-tab-button"
                    />
                    <Tab label="Payments" id="payments-tab-button" />
                    {login?.data?.role === "orgadmin" && (
                      <Tab label="Feedback" id="feedback-tab-button" />
                    )}
                    <Tab label="Workflows" id="workflows-tab-button" />
                  </Tabs>
                )}
              </Box>

              {/* Tour Button */}
              {!view && tabValue !== 0 && (
                <Tooltip title="Start Admin Panel Tour">
                  <IconButton
                    id="tour-restart-button-admin"
                    onClick={startCompleteTour}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* 0. Organisation Profile (Global Admin) */}
          {!view && login?.data?.role === "global admin" && tabValue === 0 && (
            <Box
              id="org-profile-tab"
              sx={{ mt: 2, p: 3, bgcolor: "white", borderRadius: 2 }}
            >
              {!selectedOrganization ||
              selectedOrganization === "All Organisation" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                    textAlign: "center",
                    gap: 2,
                  }}
                >
                  <BusinessIcon
                    sx={{ fontSize: 80, color: "primary.main", opacity: 0.3 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    Please select an organisation from the dropdown above to
                    view its profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a specific organisation to manage its details,
                    billing information, and settings
                  </Typography>
                </Box>
              ) : (
                <OrganisationProfileNew orgId={selectedOrganization} />
              )}
            </Box>
          )}

          {/* Manage Organisations (Global Admin) — only the GlobalAdminPage-embedded
              view; effectiveTabValue falls back to tabValue when `view` is unset, and
              tabValue also happens to land on 5 for "Workflows" in the plain /admin tab
              bar, which made this panel render on top of the Workflows tab's content. */}
          {login?.data?.role === "global admin" &&
            view === "global-admin-orgs" && (
            <ManageOrgsPanel
              filteredData={filteredData}
              editingData={editingData}
              setEditingData={setEditingData}
              searchText={searchText}
              setSearchText={setSearchText}
              openRow={openRow}
              isGlobalAdmin={isGlobalAdmin}
              isImpersonating={isImpersonating}
              enterImpersonation={enterImpersonation}
              login={login}
              organisationList={organisationList}
              handleClickOrganisation={handleClickOrganisation}
              handleExpand={handleExpand}
              handleInputChange={handleInputChange}
              handleSave={handleSave}
              handleReassignOrganisation={handleReassignOrganisation}
              handleCancel={handleCancel}
              handleDelete={handleDelete}
            />
          )}

          {/* 0. Organisation Profile (Org Admin) */}
          {!view && login?.data?.role === "orgadmin" && tabValue === 0 && (
            <div id="org-profile-tab">
              <OrganisationProfileNew />
            </div>
          )}

          {/* 5. Feedback (Org Admin) — read-only, scoped to their own org */}
          {!view && login?.data?.role === "orgadmin" && tabValue === 5 && (
            <div id="feedback-tab">
              <OrgFeedbackView />
            </div>
          )}

          {/* Workflows — last tab for both roles; index differs because Feedback
              (orgadmin-only) sits between Payments and Workflows for orgadmin. */}
          {!view &&
            ((login?.data?.role === "global admin" && tabValue === 5) ||
              (login?.data?.role === "orgadmin" && tabValue === 6)) && (
              <Box id="workflows-tab" sx={{ mt: 2 }}>
                <WorkflowsTab />
              </Box>
            )}

          {/* 1. Users (Global Admin - now at index 1) */}
          {!view && login?.data?.role === "global admin" && tabValue === 1 && (
            <GlobalAdminUsersPanel
              filteredUsers={filteredUsers}
              userSearchText={userSearchText}
              setUserSearchText={setUserSearchText}
              openUserRow={openUserRow}
              setOpenUserRow={setOpenUserRow}
              organisationList={organisationList}
              login={login}
              handleOpenModal={handleOpenModal}
              handleExpandUser={handleExpandUser}
              handleUserInputChange={handleUserInputChange}
              handleSaveUser={handleSaveUser}
              handleResendInvite={handleResendInvite}
              handleDeleteUser={handleDeleteUser}
              passwordModalOpen={passwordModalOpen}
              setPasswordModalOpen={setPasswordModalOpen}
              passwordModalValues={passwordModalValues}
              setPasswordModalValues={setPasswordModalValues}
              passwordModalError={passwordModalError}
              setPasswordModalError={setPasswordModalError}
              setPasswordModalUserId={setPasswordModalUserId}
              handleSetPassword={handleSetPassword}
            />
          )}


          {/* 1. Users (Org Admin) */}

          {!view && login?.data?.role === "orgadmin" && tabValue === 1 && (
            <OrgAdminUsersPanel
              users={users}
              searchUserText={searchUserText}
              setSearchUserText={setSearchUserText}
              openUserRow={openUserRow}
              setOpenUserRow={setOpenUserRow}
              organisationList={organisationList}
              login={login}
              handleOpenModal={handleOpenModal}
              handleExpandUser={handleExpandUser}
              handleUserInputChange={handleUserInputChange}
              handleSaveUser={handleSaveUser}
              handleResendInvite={handleResendInvite}
              handleDeleteUser={handleDeleteUser}
              passwordModalOpen={passwordModalOpen}
              setPasswordModalOpen={setPasswordModalOpen}
              passwordModalValues={passwordModalValues}
              setPasswordModalValues={setPasswordModalValues}
              passwordModalError={passwordModalError}
              setPasswordModalError={setPasswordModalError}
              setPasswordModalUserId={setPasswordModalUserId}
              handleSetPassword={handleSetPassword}
            />
          )}

          {/* Connectors Tab */}
          {!view &&
            ((login?.data?.role === "global admin" && tabValue === 2) ||
              (login?.data?.role === "orgadmin" && tabValue === 2)) && (
              <ConnectorsTabPanel
                connectorTabValue={connectorTabValue}
                setConnectorTabValue={setConnectorTabValue}
                connectorAuthorization={connectorAuthorization}
                handleCardClick={handleCardClick}
                handleToggle={handleToggle}
                selectedOrganization={selectedOrganization}
                selectedOrgObj={selectedOrgObj}
                organisationList={organisationList}
                refreshConnectors={() => handleChangeForAzure({ target: { value: selectedOrganization } })}
                login={login}
              />
            )}


          {/* Payments Tab */}
          {!view &&
            ((login?.data?.role === "global admin" && tabValue === 4) ||
              (login?.data?.role === "orgadmin" && tabValue === 4)) && (
              <Box
                id="payments-tab"
                sx={{ p: 3, fontFamily: "Inter, sans-serif" }}
              >
                {selectedOrgObj?.isBillingExempt ? (
                  <BillingExemptBanner />
                ) : selectedOrganization === "All Organisation" ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 5,
                      textAlign: "center",
                      height: "60vh",
                    }}
                  >
                    <img
                      src="/illustrations/NoPayments.png"
                      alt="Select an organisation"
                      style={{ width: "200px", marginBottom: "24px" }}
                    />
                    <Typography variant="h6" component="p">
                      Sorry! No payment method found
                    </Typography>
                  </Box>
                ) : (
                  <Stack alignItems="center" justifyContent="center">
                    <PaymentsTab
                      orgId={selectedOrganization}
                      // @ts-ignore
                      organisation={selectedOrgObj}
                    />
                  </Stack>
                )}
              </Box>
            )}

          {/* {login?.data?.role === "global admin" && tabValue === 6 && (
            <Box
              id="manage-ontology-tab"
              sx={{
                height: "calc(100vh - 240px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: 2,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
                background: "white",
              }}
            >
              <OntologyDiagram
                onTtlChange={(ttl) => {
                  // Optional: persist TTL to backend whenever it changes
                  // e.g. debounced save:
                  // debouncedSave(ttl);
                }}
              />
            </Box>
          )} */}

          {/* 6. Manage Ontology (Global Admin Only - now at index 6) */}
          {login?.data?.role === "global admin" && effectiveTabValue === 6 && (
            <OntologyTabPanel
              view={view}
              selectedOrganization={selectedOrganization}
              organisationList={organisationList}
              handleChangeForAzure={handleChangeForAzure}
              ontologyTabValue={ontologyTabValue}
              setOntologyTabValue={setOntologyTabValue}
              ontologyFileContent={ontologyFileContent}
              setOntologyFileContent={setOntologyFileContent}
              shaclFileContent={shaclFileContent}
              setShaclFileContent={setShaclFileContent}
              lastSavedOntology={lastSavedOntology}
              ontologyMeta={ontologyMeta}
              shaclMeta={shaclMeta}
              lastSavedShacl={lastSavedShacl}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              isFetching={isFetching}
              isSaving={isSaving}
              uploadingType={uploadingType}
              ontologyInputRef={ontologyInputRef}
              shaclInputRef={shaclInputRef}
              ontologyHasUnsavedChanges={ontologyHasUnsavedChanges}
              shaclHasUnsavedChanges={shaclHasUnsavedChanges}
              currentTabHasUnsavedChanges={currentTabHasUnsavedChanges}
              handleSaveOntology={handleSaveOntology}
              handleFileUpload={handleFileUpload}
            />
          )}


          {/* Manage Template */}
          {!view &&
            ((login?.data?.role === "global admin" && tabValue === 3) ||
              (login?.data?.role === "orgadmin" && tabValue === 3)) && (
              <div id="manage-template-tab">
                <UploadDocumentTemplate
                  selectedOrganization={selectedOrganization}
                  organisationList={organisationList}
                />
              </div>
            )}

          {/* 7. Feedback (Global Admin Only - now at index 7) */}
          {login?.data?.role === "global admin" && tabValue === 7 && (
            <div id="feedback-tab">
              <ProductFeedbackAdminView />
            </div>
          )}

          <OrgFormDialog
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            editData={editData}
            organisation={organisation}
            setOrganisation={setOrganisation}
            handleChange={handleChange}
            orgValidationErrors={orgValidationErrors}
            isGlobalAdmin={isGlobalAdmin}
            organisationList={organisationList}
            handleSubmit={handleSubmit}
          />
          {/* User Form Dialog */}
          <UserFormDialog
            openModalUser={openModalUser}
            setOpenModalForUser={setOpenModalForUser}
            editingUser={editingUser}
            form={form}
            setForm={setForm}
            userErrors={userErrors}
            handleChangeForUser={handleChangeForUser}
            showAddUserPassword={showAddUserPassword}
            setShowAddUserPassword={setShowAddUserPassword}
            login={login}
            organisationList={organisationList}
            proratedAmount={proratedAmount}
            userForms={userForms}
            setEmailDuplicateWarnings={setEmailDuplicateWarnings}
            userFormsErrors={userFormsErrors}
            emailDuplicateWarnings={emailDuplicateWarnings}
            isUserCardExpanded={isUserCardExpanded}
            toggleUserCard={toggleUserCard}
            removeUserEntry={removeUserEntry}
            updateUserEntry={updateUserEntry}
            checkEmailDuplicate={checkEmailDuplicate}
            addUserEntry={addUserEntry}
            addUserError={addUserError}
            handleSubmitUser={handleSubmitUser}
            userSubmitting={userSubmitting}
          />
          <ConfirmSeatDialog
            confirmSeatModal={confirmSeatModal}
            setConfirmSeatModal={setConfirmSeatModal}
            userForms={userForms}
            proratedAmount={proratedAmount}
            confirmSeatModalError={confirmSeatModalError}
            confirmSeatLoading={confirmSeatLoading}
            handleConfirmSeatAddition={handleConfirmSeatAddition}
          />

          {/* Delete User — same confirmation pattern as rest of app */}
          <ConfirmDeleteModal
            open={deleteConfirmOpen}
            entityName={deleteUserName?.trim() ? deleteUserName : "this user"}
            onCancel={() => setDeleteConfirmOpen(false)}
            onConfirm={confirmDeleteUser}
          />

          <DeleteOrgDialog
            deleteOrgConfirmOpen={deleteOrgConfirmOpen}
            cancelDeleteOrganisation={cancelDeleteOrganisation}
            deleteOrgName={deleteOrgName}
            deleteOrgConfirmText={deleteOrgConfirmText}
            setDeleteOrgConfirmText={setDeleteOrgConfirmText}
            confirmDeleteOrganisation={confirmDeleteOrganisation}
          />

          <ToggleConnectorDialog
            confirmationAction={confirmationAction}
            setConfirmationAction={setConfirmationAction}
            handleConfirmToggle={handleConfirmToggle}
          />

          <ConnectorConfigModal
            isConnectorModalOpen={isConnectorModalOpen}
            setIsConnectorModalOpen={setIsConnectorModalOpen}
            currentConnector={currentConnector}
            connectorAuthorization={connectorAuthorization}
            connectorInputs={connectorInputs}
            setConnectorInputs={setConnectorInputs}
            originalConnectorInputs={originalConnectorInputs}
            isConnectorLoading={isConnectorLoading}
            isEmailEnabled={isEmailEnabled}
            isAutoRecordingEnabled={isAutoRecordingEnabled}
            isOutlookContactsEnabled={isOutlookContactsEnabled}
            autoRecordingSetupOpen={autoRecordingSetupOpen}
            setAutoRecordingSetupOpen={setAutoRecordingSetupOpen}
            storageStats={storageStats}
            storageProvider={storageProvider}
            storageConfirmDialog={storageConfirmDialog}
            setStorageConfirmDialog={setStorageConfirmDialog}
            isSwitchingStorage={isSwitchingStorage}
            pendingStorageProvider={pendingStorageProvider}
            availableDrives={availableDrives}
            isFetchingDrives={isFetchingDrives}
            selectedDriveId={selectedDriveId}
            setSelectedDriveId={setSelectedDriveId}
            isMigrationActionLoading={isMigrationActionLoading}
            selectedOrganization={selectedOrganization}
            selectedOrgObj={selectedOrgObj}
            organisationList={organisationList}
            handleAuthorizeConnector={handleAuthorizeConnector}
            handleConnectMicrosoft365={handleConnectMicrosoft365}
            handleDisconnectMicrosoft365={handleDisconnectMicrosoft365}
            toggleEmailStatus={toggleEmailStatus}
            toggleAutoRecordingStatus={toggleAutoRecordingStatus}
            toggleOutlookContactsStatus={toggleOutlookContactsStatus}
            handleMigrationAction={handleMigrationAction}
            handleStorageProviderClick={handleStorageProviderClick}
            confirmAndStartMigration={confirmAndStartMigration}
            fetchSharePointDrives={fetchSharePointDrives}
            openSiteChangeDialog={openSiteChangeDialog}
          />

          <FileTypesDrawer
            open={open}
            setOpen={setOpen}
            fileTypes={fileTypes}
            formatKey={formatKey}
            selectedAppSetting={selectedAppSetting}
            setSelectedAppSetting={setSelectedAppSetting}
            handleSaveFileTypes={handleSaveFileTypes}
          />

          {/* Storage Provider Confirmation Dialog */}
          <StorageProviderDialog
            storageConfirmDialog={storageConfirmDialog}
            setStorageConfirmDialog={setStorageConfirmDialog}
            isMigrationActionLoading={isMigrationActionLoading}
            setPendingStorageProvider={setPendingStorageProvider}
            storageProvider={storageProvider}
            pendingStorageProvider={pendingStorageProvider}
            isFetchingDrives={isFetchingDrives}
            selectedDriveId={selectedDriveId}
            setSelectedDriveId={setSelectedDriveId}
            availableDrives={availableDrives}
            confirmAndStartMigration={confirmAndStartMigration}
          />

          {/* --- NEW: SharePoint to SharePoint Site Change Dialog --- */}
          <SharePointSiteDialog
            isSiteChangeDialogOpen={isSiteChangeDialogOpen}
            setIsSiteChangeDialogOpen={setIsSiteChangeDialogOpen}
            isMigrationActionLoading={isMigrationActionLoading}
            isFetchingDrives={isFetchingDrives}
            pendingDriveSelection={pendingDriveSelection}
            setPendingDriveSelection={setPendingDriveSelection}
            availableDrives={availableDrives}
            handleConfirmSiteChange={handleConfirmSiteChange}
            selectedOrgObj={selectedOrgObj}
          />
        </Box>
      </ContainerOrFragment>
    </>
  );
};

export default Organisations;
